/**
 * Pure helpers for the production / WIP board page. Kept separate from the component so the lane
 * config and the per-order count line are unit-tested. The board's laning + sorting happen server-side
 * (`GET /production/board`); this file only formats what the server returns.
 */
import type { components } from '@/lib/api/types.gen';

export type ProductionBoardOrder = components['schemas']['ProductionBoardOrder'];
export type OrderProduction = components['schemas']['OrderProduction'];
export type ProductionBoard = components['schemas']['ProductionBoardResponse'];

export type LaneKey = 'needsAction' | 'queued' | 'inProduction' | 'printed' | 'readyToShip';

export type LaneMeta = {
  key: LaneKey;
  title: string;
  blurb: string;
  /** Tailwind classes for the lane's count badge tone. */
  badgeClassName: string;
  /**
   * What this lane must say when the outstanding-reprint lookup failed.
   *
   * Decision D1 demotes EVERY printed/produced/packed order into `inProduction` on a failed lookup
   * (`production-board.ts:180`), so the two ship lanes come back provably empty and `inProduction`
   * fills with orders whose count line reads "5 printed". The page-level banner is the only thing
   * explaining that — and on a phone it is several screens above the lane it invalidates, so by the
   * time an operator is looking at an empty green lane the explanation is off-screen.
   *
   * Lives on the lane config rather than inline in the page so it stays unit-tested beside the lane it
   * belongs to, and so a future lane cannot be added without answering the question.
   */
  noteWhenReprintCheckFailed?: string;
};

/** Left→right = attention→done. Titles/tones mirror the queue's STATUS_META colour language. */
export const LANES: LaneMeta[] = [
  {
    key: 'needsAction',
    // "Needs mapping" is the queue's word for this state (orders-queue STATUS_META); "Unmapped"
    // appeared nowhere else in the operator-facing UI.
    title: 'Needs action',
    blurb: 'Needs mapping, blocked or unpaid — fix before it can print.',
    badgeClassName:
      'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
  },
  {
    key: 'queued',
    title: 'Queued',
    blurb: 'Ready to build into a gang sheet.',
    badgeClassName: 'border-border bg-muted text-muted-foreground',
  },
  {
    key: 'inProduction',
    // NOT "On a sheet — printing": `deriveStatus` returns IN_PRODUCTION whenever some work is done and
    // some is still open, even with `batched === 0` — so an order with garments that are on no sheet at
    // all lands here, and the build lane does not list it. The blurb has to cover both shapes.
    title: 'In production',
    blurb: 'On a sheet, or part-printed.',
    badgeClassName:
      'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300',
    noteWhenReprintCheckFailed: 'Reprint check unavailable — printed orders are held here.',
  },
  {
    key: 'printed',
    title: 'Printed · to ship',
    blurb: 'Printed, awaiting fulfilment in Shopify.',
    badgeClassName:
      'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
    noteWhenReprintCheckFailed: 'Held — reprint check unavailable.',
  },
  {
    key: 'readyToShip',
    // NOT "Caps · to ship". The lane receives BOTH `PRODUCED` (caps) and `PACKED` (ship-only stocked
    // goods — decals and similar), and SHIP_ONLY_ROLLUP is 'true' on the live Api stack, so a packed
    // decal order sits under this heading today. The per-card count line already tells the two apart
    // ("1 cap produced" vs "1 packed"), so only the heading was lying.
    title: 'Ready to ship',
    blurb: 'Caps produced, or Ship-only goods packed — awaiting fulfilment in Shopify.',
    badgeClassName:
      'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300',
    noteWhenReprintCheckFailed: 'Held — reprint check unavailable.',
  },
];

/**
 * NAMES the blocker on a needs-action order instead of counting it.
 *
 * "1 needs action" told an operator that something was wrong and nothing else — and the three causes
 * are three different people's jobs: map the product (a catalog task), unblock it (a product problem),
 * chase the payment (an office task). Whoever was looking at the board had to open the order to learn
 * which, on the one lane whose entire purpose is triage.
 *
 * Names the cause only when the order has EXACTLY ONE kind, because "2 need mapping · 1 unpaid" on a
 * card this size is worse than the honest summary — a mixed order genuinely does need someone to look.
 * The server guarantees `needsMapping + blocked + unpaid === needsAction`, so the fallback is reached
 * only for a genuine mix, never for an unclassified remainder.
 */
export function blockerLabel(p: OrderProduction): string {
  const kinds = [
    { n: p.needsMapping, one: 'needs mapping', many: 'need mapping' },
    { n: p.blocked, one: 'blocked', many: 'blocked' },
    { n: p.unpaid, one: 'unpaid', many: 'unpaid' },
  ].filter((k) => k.n > 0);
  const only = kinds.length === 1 ? kinds[0] : undefined;
  if (only) return `${only.n} ${only.n === 1 ? only.one : only.many}`;
  return `${p.needsAction} need${p.needsAction === 1 ? 's' : ''} action`;
}

/**
 * GARMENTS on the card, because every other number here counts LINES and "3 batched" could be 3 shirts
 * or 300. `null` when there is nothing to say (no producible garments — e.g. an all-decal order), so
 * the caller renders no line at all rather than "0 pieces".
 *
 * ⚠️ "· N to make" is appended ONLY when some of the work is done and some is not. Two suppressions,
 * both deliberate:
 *  - `outstanding === 0` — otherwise the `shippedUnproduced` warning lane, where EVERY line is FULFILLED
 *    (and so contributes to `pieces` but never to `outstandingPieces`), would render "7 pieces · 0 to
 *    make" on the one lane that exists to say "this shipped WITHOUT being produced, go chase it". The
 *    amber "N shipped · not printed" badge is that lane's number.
 *  - `outstanding === pieces` — a queued order where nothing is done yet says "8 pieces"; appending
 *    "· 8 to make" is the same fact twice.
 */
export function productionPieceLine(p: OrderProduction): string | null {
  if (p.pieces <= 0) return null;
  const head = `${p.pieces} piece${p.pieces === 1 ? '' : 's'}`;
  if (p.outstandingPieces <= 0 || p.outstandingPieces >= p.pieces) return head;
  return `${head} · ${p.outstandingPieces} to make`;
}

/**
 * Compact per-order count line summarising the order's line states, e.g. "3 batched · 1 printed · 2
 * open". Shows only non-zero buckets, worst-first, so a mixed order (some open + some printed) reads
 * at a glance. Falls back to the line count when nothing producible is outstanding.
 */
export function productionCountLine(p: OrderProduction): string {
  const parts: string[] = [];
  if (p.needsAction) parts.push(blockerLabel(p));
  if (p.open) parts.push(`${p.open} to build`);
  if (p.nonDtfOpen) parts.push(`${p.nonDtfOpen} cap${p.nonDtfOpen === 1 ? '' : 's'} to produce`);
  if (p.fulfillOpen) parts.push(`${p.fulfillOpen} to pack`);
  // "batched" is a word this app invented; "on a sheet" is what the shop says, and it is the
  // wording the approved mockup uses. Same for `open` → "to build" above.
  if (p.batched) parts.push(`${p.batched} on a sheet`);
  if (p.printed) parts.push(`${p.printed} printed`);
  if (p.nonDtfProduced)
    parts.push(`${p.nonDtfProduced} cap${p.nonDtfProduced === 1 ? '' : 's'} produced`);
  if (p.fulfillPacked) parts.push(`${p.fulfillPacked} packed`);
  if (p.alreadyPrinted) parts.push(`${p.alreadyPrinted} printed off-system`);
  if (p.shipped) parts.push(`${p.shipped} shipped`);
  if (p.fulfilled) parts.push(`${p.fulfilled} fulfilled`);
  if (parts.length > 0) return parts.join(' · ');
  return `${p.totalLines} line${p.totalLines === 1 ? '' : 's'}`;
}

/** Lane filter state: one lane, or the whole board side by side. */
export type LaneSelection = LaneKey | 'all';

const LANE_KEYS = new Set<string>(LANES.map((l) => l.key));

/**
 * Which lane the board should show.
 *
 * The board is five lanes wide on a desktop and, below `md`, five lanes STACKED — so on a phone every
 * order in the pipeline sits in one column and reaching "In production" means scrolling past everything
 * queued. Measured on the live board that is 27 cards before the lane you wanted. The fix is to show one
 * lane at a time on a narrow screen, with the chip strip carrying every lane's count so the board's
 * shape — the thing a board is actually for — is still legible at a glance.
 *
 * So `'all'` is a DESKTOP-ONLY selection: on a narrow screen it resolves to the first lane that has
 * work, because "all" there means the stacked layout this exists to replace. An operator can still pick
 * an empty lane explicitly (it is a legitimate question — "is anything ready to ship?"); this only
 * decides where they LAND.
 *
 * Pure, and takes `raw: unknown` because the value comes off the URL: an unrecognised `?lane=` must
 * fall back, never throw.
 */
export function selectedLane(
  raw: unknown,
  counts: Readonly<Record<LaneKey, number>>,
  isNarrow: boolean,
): LaneSelection {
  if (typeof raw === 'string' && LANE_KEYS.has(raw)) return raw as LaneKey;
  if (!isNarrow) return 'all';
  // ⚠️ LANES order is attention→done, so "first with work" lands on the most urgent non-empty lane
  // rather than on whatever happens to be leftmost.
  const firstWithWork = LANES.find((l) => (counts[l.key] ?? 0) > 0);
  // An empty board still has to land somewhere. Named rather than `LANES[0].key` because
  // noUncheckedIndexedAccess makes that possibly-undefined; a test pins the two together so a
  // reordering of LANES cannot silently move the landing lane away from the attention one.
  return firstWithWork?.key ?? FALLBACK_LANE;
}

/** Where an empty board lands. Must stay equal to `LANES[0].key` — see the test. */
export const FALLBACK_LANE: LaneKey = 'needsAction';

/** Client-side board filter. Both fields optional; absent/blank means "no constraint". */
export type BoardFilter = { q?: string; store?: string };

/**
 * Does this order survive the toolbar filter?
 *
 * Filtering is client-side over the already-materialised response — no new request, no backend change.
 * The board is the ONLY surface that can see an order once it is claimed onto a gang sheet (a batched
 * line drops off the open-work queue), so "where is #L-7263?" had no answer anywhere in the app except
 * eyeballing five columns.
 *
 * Matches `orderName` and `storeId`, case-insensitively, on substring — an operator types "7263", not
 * "#L-7263". Whitespace-only input is not a constraint; treating it as one would empty the board on a
 * stray space.
 */
export function matchesBoardFilter(order: ProductionBoardOrder, filter: BoardFilter): boolean {
  // Compares the raw storeId, never the label — the value round-trips through `?store=` in the URL and
  // a brand label can be renamed in /stores at any time, which would silently break saved links.
  if (filter.store && order.storeId !== filter.store) return false;
  const q = filter.q?.trim().toLowerCase();
  if (!q) return true;
  return (
    order.orderName.toLowerCase().includes(q) ||
    order.storeId.toLowerCase().includes(q) ||
    // The card now SHOWS "Low Trendz", so typing it has to find something. Searching only the slug
    // meant the one string on screen was the one string that did not match.
    (order.brandLabel?.toLowerCase().includes(q) ?? false)
  );
}

/** True when the filter constrains anything — drives the "{shown} of {total}" badges. */
export function boardFilterActive(filter: BoardFilter): boolean {
  return Boolean(filter.store) || Boolean(filter.q?.trim());
}

/** One option in the store filter: the raw id it filters on, and the human label it shows. */
export type BoardStoreOption = { id: string; label: string };

/**
 * Distinct stores across every lane — the store Select's options.
 *
 * ⚠️ `id` and `label` are SEPARATE and must stay so. The id is the filter value and lives in the URL
 * (`?store=`); the label is display only and is editable in /stores. Collapsing them would make a saved
 * board link break the day someone renames a brand. The dropdown was the board's second raw-slug
 * surface — fixing only the card would have left the filter offering "low-trendz" beside cards reading
 * "Low Trendz".
 *
 * Sorted by LABEL, since that is the ordering the operator sees. Falls back to the id when no label
 * reached us, which is exactly today's rendering.
 */
export function boardStoreOptions(board: ProductionBoard): BoardStoreOption[] {
  const byId = new Map<string, string>();
  const add = (o: ProductionBoardOrder) => {
    // First non-empty label wins; every order of a store carries the same one, so this only matters
    // when the join partially failed.
    if (!byId.get(o.storeId)) byId.set(o.storeId, o.brandLabel ?? o.storeId);
  };
  for (const key of LANES) for (const o of board[key.key]) add(o);
  for (const o of board.shippedUnproduced) add(o);
  return [...byId]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id));
}

export type ProductionBoardSheet = NonNullable<ProductionBoardOrder['sheets']>[number];

/**
 * What a sheet is called on the card. "Sheet #75" when the batch carries a seq — which every live batch
 * does (measured 22/22, 2026-08-09) — and a short id otherwise, because a pre-counter batch still has to
 * be nameable enough to click. Never the bare UUID: 36 characters in a lane this narrow is not a label.
 */
export function sheetLabel(s: ProductionBoardSheet): string {
  return s.seq ? `Sheet #${s.seq}` : `Sheet ${s.batchId.slice(0, 6)}`;
}

/**
 * Operator wording for a batch status, so the chip answers "has it printed yet?" without a second click.
 * A Record over the union rather than a lookup with a fallback: adding a BatchStatus member fails the
 * build here instead of rendering the raw SCREAMING_CASE enum at someone.
 */
export const SHEET_STATUS_LABEL: Record<ProductionBoardSheet['status'], string> = {
  DRAFT: 'draft',
  COMPOSITING: 'building',
  READY: 'ready to print',
  PRINTED: 'printed',
  FAILED: 'failed',
  SHIPPED: 'shipped',
};

/**
 * Garments across the whole pipeline — the "· 123 pieces" half of the header line.
 *
 * ⚠️ THE FIVE PIPELINE LANES ONLY, never `shippedUnproduced`. That lane is ADDITIVE: a partially
 * shipped order appears in it AND in its normal lane, so summing all six double-counts exactly the
 * orders the warning exists to highlight. It is the same reason the server keeps `shippedUnproduced`
 * out of `totalOrders`, and this number has to pair with that one.
 */
export function boardPieceTotal(board: ProductionBoard): number {
  let pieces = 0;
  for (const lane of LANES) for (const o of board[lane.key]) pieces += o.production.pieces;
  return pieces;
}
